/** CTH (Casing / Tubing) and RSC (Rotary Shouldered Connection) thread inspection form + PDF types */

export type CTHVisualResult = 'OK' | 'REJ' | ''
export type CTHSideRecommendation = '' | 'reface' | 'recut' | 'decommission'
export type CTHInspectedSides = 'pin' | 'box' | 'both'
export type ThreadInspectionKind = 'CTH' | 'RSC'

export const THREAD_INSPECTION_KIND_OPTIONS = [
  { value: 'CTH', label: 'CTH' },
  { value: 'RSC', label: 'RSC' },
] as const satisfies ReadonlyArray<{ value: ThreadInspectionKind; label: string }>

export const CTH_INSPECTED_SIDE_OPTIONS = [
  { value: 'pin', label: 'PIN' },
  { value: 'box', label: 'BOX' },
  { value: 'both', label: 'PIN & BOX' },
] as const satisfies ReadonlyArray<{ value: CTHInspectedSides; label: string }>

export function cthIncludesPin(sides: CTHInspectedSides = 'both'): boolean {
  return sides === 'pin' || sides === 'both'
}

export function cthIncludesBox(sides: CTHInspectedSides = 'both'): boolean {
  return sides === 'box' || sides === 'both'
}

export function inspectedSidesFromThreadTypes(
  pinType: string | null | undefined,
  boxType: string | null | undefined,
): CTHInspectedSides {
  const hasPin = Boolean(pinType?.trim())
  const hasBox = Boolean(boxType?.trim())
  if (hasPin && hasBox) return 'both'
  if (hasPin) return 'pin'
  if (hasBox) return 'box'
  return 'both'
}

export interface CTHMeasurementPair {
  no1: string
  no2: string
}

export interface CTHVisualPair {
  no1: CTHVisualResult
  no2: CTHVisualResult
}

export interface CTHPinDimensional {
  connection: CTHMeasurementPair
  od: CTHMeasurementPair
  bevelDia: CTHMeasurementPair
  neckSrgDiamE1: CTHMeasurementPair
  neckSrgDiamE2: CTHMeasurementPair
  sealWidth: CTHMeasurementPair
  pinLength: CTHMeasurementPair
  pinNoseDia: CTHMeasurementPair
  tongSpace: CTHMeasurementPair
  benchmark: CTHMeasurementPair
  refaced: CTHMeasurementPair
}

export interface CTHBoxDimensional {
  connection: CTHMeasurementPair
  od: CTHMeasurementPair
  bevelDiam: CTHMeasurementPair
  cboreDepth: CTHMeasurementPair
  cboreDiam: CTHMeasurementPair
  sealWidth: CTHMeasurementPair
  borebackDepth: CTHMeasurementPair
  borebackDiam: CTHMeasurementPair
  tongSpace: CTHMeasurementPair
  benchmark: CTHMeasurementPair
  refaced: CTHMeasurementPair
}

export interface CTHDimensionalRow {
  pinField: keyof CTHPinDimensional
  pinLabel: string
  boxField: keyof CTHBoxDimensional
  boxLabel: string
  rscOnly?: boolean
}

export const CTH_DIMENSIONAL_ROWS: readonly CTHDimensionalRow[] = [
  { pinField: 'connection', pinLabel: 'Connection', boxField: 'connection', boxLabel: 'Connection' },
  { pinField: 'od', pinLabel: 'OD (A)', boxField: 'od', boxLabel: 'OD (A)' },
  { pinField: 'bevelDia', pinLabel: 'Bevel dia.', boxField: 'bevelDiam', boxLabel: 'Bevel diam. (G)', rscOnly: true },
  { pinField: 'neckSrgDiamE1', pinLabel: 'Neck/SRG diam. (E1)', boxField: 'cboreDepth', boxLabel: "C'Bore depth (D)", rscOnly: true },
  { pinField: 'neckSrgDiamE2', pinLabel: 'Neck/SRG diam. (E2)', boxField: 'cboreDiam', boxLabel: "C'Bore diam. (C)", rscOnly: true },
  { pinField: 'sealWidth', pinLabel: 'Seal width', boxField: 'sealWidth', boxLabel: 'Seal width (H)', rscOnly: true },
  { pinField: 'pinLength', pinLabel: 'PIN length (I)', boxField: 'borebackDepth', boxLabel: 'Boreb. C. depth*(F2)', rscOnly: true },
  { pinField: 'pinNoseDia', pinLabel: 'PIN Nose dia. (H)', boxField: 'borebackDiam', boxLabel: 'Boreb.C. diam.(F1)', rscOnly: true },
  { pinField: 'tongSpace', pinLabel: 'Tong Space (DP)', boxField: 'tongSpace', boxLabel: 'Tong Space (DB)', rscOnly: true },
  { pinField: 'benchmark', pinLabel: 'Benchmark', boxField: 'benchmark', boxLabel: 'Benchmark', rscOnly: true },
  { pinField: 'refaced', pinLabel: 'Refaced', boxField: 'refaced', boxLabel: 'Refaced', rscOnly: true },
]

export function dimensionalRowsForKind(kind: ThreadInspectionKind): readonly CTHDimensionalRow[] {
  if (kind === 'RSC') return CTH_DIMENSIONAL_ROWS
  return CTH_DIMENSIONAL_ROWS.filter((row) => !row.rscOnly)
}

export interface CTHPinVisual {
  bevelWidth: CTHVisualPair
  threadSurface: CTHVisualPair
  threadProfile: CTHVisualPair
  lead: CTHVisualPair
  baseSurface: CTHVisualPair
  sealSurface: CTHVisualPair
  condition: CTHVisualPair
  mpi: CTHVisualPair
}

export interface CTHBoxVisual {
  bevelWidth: CTHVisualPair
  threadSurface: CTHVisualPair
  threadProfile: CTHVisualPair
  boxSwell: CTHVisualPair
  lead: CTHVisualPair
  baseSurface: CTHVisualPair
  reliefSurface: CTHVisualPair
  sealSurface: CTHVisualPair
  condition: CTHVisualPair
  mpi: CTHVisualPair
}

export interface CTHBodyVisual {
  slipUpsetArea: CTHVisualResult
  mpiExternal: CTHVisualResult
}

export interface CTHVisualRow {
  pinKey: keyof CTHPinVisual
  pinLabel: string
  boxKey: keyof CTHBoxVisual
  boxLabel: string
  boxFixed?: 'N/A'
  bodyKey?: keyof CTHBodyVisual
  bodyLabel?: string
}

export const CTH_VISUAL_ROWS: readonly CTHVisualRow[] = [
  { pinKey: 'threadSurface', pinLabel: 'Thread Surface', boxKey: 'threadSurface', boxLabel: 'Thread Surface' },
  { pinKey: 'threadProfile', pinLabel: 'Thread Profile', boxKey: 'threadProfile', boxLabel: 'Thread Profile' },
  { pinKey: 'lead', pinLabel: 'Lead', boxKey: 'lead', boxLabel: 'Lead' },
  { pinKey: 'baseSurface', pinLabel: 'Base Surface', boxKey: 'baseSurface', boxLabel: 'Base Surface' },
  { pinKey: 'sealSurface', pinLabel: 'Seal Surface', boxKey: 'sealSurface', boxLabel: 'Seal Surface' },
  { pinKey: 'condition', pinLabel: 'Condition', boxKey: 'condition', boxLabel: 'Condition' },
]

export const RSC_VISUAL_ROWS: readonly CTHVisualRow[] = [
  { pinKey: 'bevelWidth', pinLabel: 'Bevel Width', boxKey: 'bevelWidth', boxLabel: 'Bevel Width' },
  { pinKey: 'threadSurface', pinLabel: 'Thread Surface', boxKey: 'threadSurface', boxLabel: 'Thread Surface' },
  { pinKey: 'threadProfile', pinLabel: 'Thread Profile', boxKey: 'boxSwell', boxLabel: 'Box Swell' },
  { pinKey: 'lead', pinLabel: 'Lead', boxKey: 'lead', boxLabel: 'Lead' },
  { pinKey: 'baseSurface', pinLabel: 'Base/SRG Surface', boxKey: 'reliefSurface', boxLabel: 'Relief Surface' },
  { pinKey: 'sealSurface', pinLabel: 'Seal Surface', boxKey: 'sealSurface', boxLabel: 'Seal Surface', boxFixed: 'N/A' },
  { pinKey: 'condition', pinLabel: 'Condition', boxKey: 'condition', boxLabel: 'Condition', bodyKey: 'slipUpsetArea', bodyLabel: 'Slip/Upset Area' },
  { pinKey: 'mpi', pinLabel: 'MPI', boxKey: 'mpi', boxLabel: 'MPI', bodyKey: 'mpiExternal', bodyLabel: 'MPI external' },
]

export function visualRowsForKind(kind: ThreadInspectionKind): readonly CTHVisualRow[] {
  return kind === 'RSC' ? RSC_VISUAL_ROWS : CTH_VISUAL_ROWS
}

export interface MpiInspectionRow {
  label: string
  value: string
}

function mpiSlots(values: CTHVisualResult[]): { ok: boolean; rej: boolean } {
  return {
    ok: values.some((value) => value === 'OK'),
    rej: values.some((value) => value === 'REJ'),
  }
}

export function buildMpiInspectionTable(
  form: Pick<CTHFormData, 'inspectionKind' | 'inspectedSides' | 'visualPin' | 'visualBox' | 'visualBody' | 'material' | 'issuedBy'>,
  options?: { pinNo2?: boolean; boxNo2?: boolean; operatorCertNo?: string },
): { visible: boolean; rows: MpiInspectionRow[] } {
  const kind = form.inspectionKind ?? 'CTH'
  const visualRows = visualRowsForKind(kind)
  const pinValues = cthIncludesPin(form.inspectedSides) && visualRows.some((row) => row.pinKey === 'mpi')
    ? [form.visualPin.mpi.no1, ...(options?.pinNo2 ? [form.visualPin.mpi.no2] : [])]
    : []
  const boxValues = cthIncludesBox(form.inspectedSides) && visualRows.some((row) => row.boxKey === 'mpi')
    ? [form.visualBox.mpi.no1, ...(options?.boxNo2 ? [form.visualBox.mpi.no2] : [])]
    : []
  const bodyValues = visualRows.some((row) => row.bodyKey === 'mpiExternal')
    ? [form.visualBody.mpiExternal]
    : []
  const pin = mpiSlots(pinValues)
  const box = mpiSlots(boxValues)
  const body = mpiSlots(bodyValues)
  const toolEndCount = (pin.ok ? 1 : 0) + (box.ok ? 1 : 0)
  const testedArea = [
    toolEndCount === 1 ? 'Tool end' : '',
    toolEndCount > 1 ? 'Tool ends' : '',
    body.ok ? 'External' : '',
  ].filter(Boolean).join(', ')

  return {
    visible: pin.ok || box.ok || body.ok,
    rows: [
      { label: 'Tested area', value: testedArea },
      { label: 'Material', value: form.material.trim() || 'Carbon Steel' },
      { label: 'Surface Condition', value: 'Machined' },
      { label: 'Test Equipment', value: 'Coil, WI. WDV14KP, Yoke' },
      { label: 'Contrast', value: 'UV Light 1500 µW/Cm2' },
      { label: 'Procedure', value: 'DS-1 Volume 4, 7.17' },
      { label: 'Acceptance', value: 'DS-1 Volume 4, ch. 7.17.4.g.' },
      { label: 'Temperature', value: '18 °C' },
      { label: 'White Light', value: '18 LUX' },
      { label: 'Serial No.', value: 'C428 / 2291' },
      { label: 'UV / LUX Gauge', value: 'MR 454 UVA/LUX' },
      { label: 'Serial No.', value: 'G0173 & S0173' },
      { label: 'Gauss meter', value: 'F.W Bell 5170' },
      { label: 'Serial No.', value: '718009' },
      { label: 'Operator', value: options?.operatorCertNo?.trim() || '' },
      { label: 'Demag.', value: 'to +/- 3 Gauss' },
      { label: 'Field Strength', value: '2,41-4,761 KA/m' },
      { label: 'Magnetic Ink', value: 'FLUXO 6' },
      { label: 'Batch No.', value: '267890' },
      { label: 'Concentration', value: '0.11 ml' },
      { label: 'Result of MT', value: pin.rej || box.rej || body.rej ? 'Rejected' : 'Accepted' },
    ],
  }
}

export const OCTG_CATEGORY_CODE: Record<ThreadInspectionKind, string> = {
  RSC: 'OCTG-1',
  CTH: 'OCTG-2',
}

export interface OctgSpecEntry {
  specKey: string
  value: string
  numeric: boolean
}

function pushMeasurement(
  rows: Array<[string, string, boolean]>,
  specKey: string,
  pair: CTHMeasurementPair,
  numeric: boolean,
) {
  const no1 = pair.no1.trim()
  const no2 = pair.no2.trim()
  if (no1) rows.push([specKey, no1, numeric])
  rows.push([`${specKey}_no2`, no2, numeric])
}

export function octgSpecEntries(form: CTHFormData): OctgSpecEntry[] {
  const rows: Array<[string, string, boolean]> = []
  pushMeasurement(rows, 'octg_pin_connection', form.pin.connection, false)
  pushMeasurement(rows, 'octg_pin_od', form.pin.od, true)
  pushMeasurement(rows, 'octg_pin_bevel_dia', form.pin.bevelDia, true)
  pushMeasurement(rows, 'octg_pin_neck_srg_e1', form.pin.neckSrgDiamE1, true)
  pushMeasurement(rows, 'octg_pin_neck_srg_e2', form.pin.neckSrgDiamE2, true)
  pushMeasurement(rows, 'octg_pin_seal_width', form.pin.sealWidth, true)
  pushMeasurement(rows, 'octg_pin_length', form.pin.pinLength, true)
  pushMeasurement(rows, 'octg_pin_nose_dia', form.pin.pinNoseDia, true)
  pushMeasurement(rows, 'octg_pin_tong_space', form.pin.tongSpace, true)
  pushMeasurement(rows, 'octg_pin_benchmark', form.pin.benchmark, false)
  pushMeasurement(rows, 'octg_pin_refaced', form.pin.refaced, false)
  pushMeasurement(rows, 'octg_box_connection', form.box.connection, false)
  pushMeasurement(rows, 'octg_box_od', form.box.od, true)
  pushMeasurement(rows, 'octg_box_bevel_diam', form.box.bevelDiam, true)
  pushMeasurement(rows, 'octg_box_cbore_depth', form.box.cboreDepth, true)
  pushMeasurement(rows, 'octg_box_cbore_diam', form.box.cboreDiam, true)
  pushMeasurement(rows, 'octg_box_seal_width', form.box.sealWidth, true)
  pushMeasurement(rows, 'octg_box_boreback_depth', form.box.borebackDepth, true)
  pushMeasurement(rows, 'octg_box_boreback_diam', form.box.borebackDiam, true)
  pushMeasurement(rows, 'octg_box_tong_space', form.box.tongSpace, true)
  pushMeasurement(rows, 'octg_box_benchmark', form.box.benchmark, false)
  pushMeasurement(rows, 'octg_box_refaced', form.box.refaced, false)
  const toolLength = form.bodyToolLength.trim()
  const bodyOd = form.bodyOd.trim()
  if (toolLength) rows.push(['octg_body_tool_length', toolLength, true])
  if (bodyOd) rows.push(['octg_body_od', bodyOd, true])
  if (form.visualBody.slipUpsetArea) rows.push(['octg_slip_upset_area', form.visualBody.slipUpsetArea, false])
  if (form.visualBody.mpiExternal) rows.push(['octg_mpi_external', form.visualBody.mpiExternal, false])
  return rows.map(([specKey, value, numeric]) => ({ specKey, value, numeric }))
}

const OCTG_PIN_SPEC_FIELDS = {
  octg_pin_connection: 'connection',
  octg_pin_od: 'od',
  octg_pin_bevel_dia: 'bevelDia',
  octg_pin_neck_srg_e1: 'neckSrgDiamE1',
  octg_pin_neck_srg_e2: 'neckSrgDiamE2',
  octg_pin_seal_width: 'sealWidth',
  octg_pin_length: 'pinLength',
  octg_pin_nose_dia: 'pinNoseDia',
  octg_pin_tong_space: 'tongSpace',
  octg_pin_benchmark: 'benchmark',
  octg_pin_refaced: 'refaced',
} as const satisfies Record<string, keyof CTHPinDimensional>

const OCTG_BOX_SPEC_FIELDS = {
  octg_box_connection: 'connection',
  octg_box_od: 'od',
  octg_box_bevel_diam: 'bevelDiam',
  octg_box_cbore_depth: 'cboreDepth',
  octg_box_cbore_diam: 'cboreDiam',
  octg_box_seal_width: 'sealWidth',
  octg_box_boreback_depth: 'borebackDepth',
  octg_box_boreback_diam: 'borebackDiam',
  octg_box_tong_space: 'tongSpace',
  octg_box_benchmark: 'benchmark',
  octg_box_refaced: 'refaced',
} as const satisfies Record<string, keyof CTHBoxDimensional>

export interface OctgStoredSpec {
  spec_key: string
  value_text: string | null
  value_numeric: number | string | null
}

function storedSpecText(spec: OctgStoredSpec): string {
  const text = spec.value_text?.trim()
  if (text) return text
  if (spec.value_numeric == null || spec.value_numeric === '') return ''
  const numeric = Number(spec.value_numeric)
  if (!Number.isFinite(numeric) || numeric === 0) return ''
  return String(spec.value_numeric)
}

function pairFromStored(base: string, extra = ''): CTHMeasurementPair {
  const storedExtra = extra.trim()
  if (base.includes(' / ')) {
    const [no1, legacyNo2] = base.split(' / ').map((part) => part.trim())
    return { no1: no1 || '', no2: storedExtra || legacyNo2 || '' }
  }
  return { no1: base.trim(), no2: storedExtra }
}

function blankPairs<T extends CTHPinDimensional | CTHBoxDimensional>(side: T): T {
  const next = { ...side } as Record<string, CTHMeasurementPair>
  for (const key of Object.keys(side)) next[key] = { no1: '', no2: '' }
  return next as unknown as T
}

function blankVisual<T extends CTHPinVisual | CTHBoxVisual>(visual: T): T {
  const next = { ...visual } as Record<string, CTHVisualPair>
  for (const key of Object.keys(visual)) next[key] = { no1: '', no2: '' }
  return next as unknown as T
}

function hasNo2(side: CTHPinDimensional | CTHBoxDimensional): boolean {
  return Object.values(side).some((pair) => pair.no2.trim())
}

export function applyOctgSpecs(
  form: CTHFormData,
  specs: OctgStoredSpec[],
  fallback?: { pinConnection?: string | null; boxConnection?: string | null },
): { form: CTHFormData; pinNo2: boolean; boxNo2: boolean } {
  const byKey = new Map(specs.map((spec) => [spec.spec_key, storedSpecText(spec)]))
  const pin = blankPairs(form.pin)
  const box = blankPairs(form.box)
  for (const [specKey, field] of Object.entries(OCTG_PIN_SPEC_FIELDS)) {
    pin[field] = pairFromStored(byKey.get(specKey) || '', byKey.get(`${specKey}_no2`) || '')
  }
  for (const [specKey, field] of Object.entries(OCTG_BOX_SPEC_FIELDS)) {
    box[field] = pairFromStored(byKey.get(specKey) || '', byKey.get(`${specKey}_no2`) || '')
  }
  if (!pin.connection.no1 && fallback?.pinConnection?.trim()) {
    pin.connection = { no1: fallback.pinConnection.trim(), no2: pin.connection.no2 }
  }
  if (!box.connection.no1 && fallback?.boxConnection?.trim()) {
    box.connection = { no1: fallback.boxConnection.trim(), no2: box.connection.no2 }
  }

  const slip = byKey.get('octg_slip_upset_area') || ''
  const mpi = byKey.get('octg_mpi_external') || ''
  const pinConnection = pin.connection.no1 || pin.connection.no2
  const boxConnection = box.connection.no1 || box.connection.no2

  return {
    form: {
      ...form,
      pin,
      box,
      bodyToolLength: byKey.get('octg_body_tool_length') || '',
      bodyOd: byKey.get('octg_body_od') || '',
      visualPin: blankVisual(form.visualPin),
      visualBox: blankVisual(form.visualBox),
      visualBody: {
        slipUpsetArea: slip === 'OK' || slip === 'REJ' ? slip : '',
        mpiExternal: mpi === 'OK' || mpi === 'REJ' ? mpi : '',
      },
      inspectedSides: inspectedSidesFromThreadTypes(pinConnection, boxConnection),
      pinRecommendation: '',
      boxRecommendation: '',
    },
    pinNo2: hasNo2(pin),
    boxNo2: hasNo2(box),
  }
}

export interface CTHCompany {
  id: string
  name: string
  report_code: string | null
}

export interface CTHInspector {
  id: string
  first_name: string
  last_name: string
}

export interface CTHFormData {
  testDate: string
  testSite: string
  company: string
  reportNumber: string
  report_code: string | null
  objectDescription: string
  assemblyDescription: string
  material: string
  serialNo: string
  partNo: string
  comments: string
  inspectionKind: ThreadInspectionKind
  inspectedSides: CTHInspectedSides
  pin: CTHPinDimensional
  box: CTHBoxDimensional
  bodyToolLength: string
  bodyOd: string
  visualPin: CTHPinVisual
  visualBox: CTHBoxVisual
  visualBody: CTHBodyVisual
  pinRecommendation: CTHSideRecommendation
  boxRecommendation: CTHSideRecommendation
  issuedBy: string
  verifiedBy: string
  attachedImage?: string
}

export interface CTHThreadInspectionData {
  companyName?: string
  objectDescription: string
  reportNo: string
  assemblyDescription: string
  testSite: string
  material: string
  testDate: string
  serialNo: string
  operator: string
  partNo: string
  testProcedure: string
  acceptance: string
  comments: string
  inspectionKind?: ThreadInspectionKind
  inspectedSides?: CTHInspectedSides
  pin: CTHPinDimensional
  box: CTHBoxDimensional
  bodyToolLength?: string
  bodyOd?: string
  pinConnection: CTHMeasurementPair
  pinOD: CTHMeasurementPair
  boxConnection: CTHMeasurementPair
  boxOD: CTHMeasurementPair
  visualPin: CTHPinVisual
  visualBox: CTHBoxVisual
  visualBody?: CTHBodyVisual
  mpiInspection?: MpiInspectionRow[]
  testAccepted: number
  testRejected: number
  testRefaceRecommended: number
  testRecutRecommended: number
  testScrapRecommended: number
  testAcceptedLabel: string
  testRejectedLabel: string
  testAcceptedNote: string
  testRefaceNote: string
  testRecutNote: string
  testScrapNote: string
  inspectorName: string
  issuedBy?: string
  verifiedBy?: string
  attachedImage?: string
}

export const CTH_INSPECTION_COMMENTS =
  'Connections on tool ends inspected and MPI tested. When drill pipes/subs pieces are checked in addition sub/upset area shall be VT and MT inspected. Measurements in mm. **Boreback depth=Box depth/dia on double shoulderd connections.'

export const CTH_TEST_PROCEDURE_ACCEPTANCE = 'DS-1 Volume 4.7.14; 7.16 & 7.17'

const emptyPair = (): CTHMeasurementPair => ({ no1: '', no2: '' })
const emptyVisualPair = (): CTHVisualPair => ({ no1: '', no2: '' })

export function sideHasNo2Data(
  dimensional: CTHPinDimensional | CTHBoxDimensional,
  visual: CTHPinVisual | CTHBoxVisual,
): boolean {
  const pairs = Object.values(dimensional) as CTHMeasurementPair[]
  if (pairs.some((pair) => pair.no2.trim())) return true
  const visualPairs = Object.values(visual) as CTHVisualPair[]
  return visualPairs.some((pair) => pair.no2 === 'OK' || pair.no2 === 'REJ')
}

export function clearSideNo2<
  D extends CTHPinDimensional | CTHBoxDimensional,
  V extends CTHPinVisual | CTHBoxVisual,
>(
  dimensional: D,
  visual: V,
): { dimensional: D; visual: V } {
  const next = Object.fromEntries(
    (Object.entries(dimensional) as [string, CTHMeasurementPair][]).map(([key, pair]) => [key, { ...pair, no2: '' }]),
  ) as unknown as D
  const nextVisual = Object.fromEntries(
    (Object.entries(visual) as [string, CTHVisualPair][]).map(([key, pair]) => [key, { ...pair, no2: '' }]),
  ) as unknown as V
  return { dimensional: next, visual: nextVisual }
}

export function enableSideVisualNo2<V extends CTHPinVisual | CTHBoxVisual>(visual: V): V {
  return visual
}

const createEmptyPinDimensional = (): CTHPinDimensional => ({
  connection: emptyPair(),
  od: emptyPair(),
  bevelDia: emptyPair(),
  neckSrgDiamE1: emptyPair(),
  neckSrgDiamE2: emptyPair(),
  sealWidth: emptyPair(),
  pinLength: emptyPair(),
  pinNoseDia: emptyPair(),
  tongSpace: emptyPair(),
  benchmark: emptyPair(),
  refaced: emptyPair(),
})

const createEmptyBoxDimensional = (): CTHBoxDimensional => ({
  connection: emptyPair(),
  od: emptyPair(),
  bevelDiam: emptyPair(),
  cboreDepth: emptyPair(),
  cboreDiam: emptyPair(),
  sealWidth: emptyPair(),
  borebackDepth: emptyPair(),
  borebackDiam: emptyPair(),
  tongSpace: emptyPair(),
  benchmark: emptyPair(),
  refaced: emptyPair(),
})

const createEmptyPinVisual = (): CTHPinVisual => ({
  bevelWidth: emptyVisualPair(),
  threadSurface: emptyVisualPair(),
  threadProfile: emptyVisualPair(),
  lead: emptyVisualPair(),
  baseSurface: emptyVisualPair(),
  sealSurface: emptyVisualPair(),
  condition: emptyVisualPair(),
  mpi: emptyVisualPair(),
})

const createEmptyBoxVisual = (): CTHBoxVisual => ({
  bevelWidth: emptyVisualPair(),
  threadSurface: emptyVisualPair(),
  threadProfile: emptyVisualPair(),
  boxSwell: emptyVisualPair(),
  lead: emptyVisualPair(),
  baseSurface: emptyVisualPair(),
  reliefSurface: emptyVisualPair(),
  sealSurface: emptyVisualPair(),
  condition: emptyVisualPair(),
  mpi: emptyVisualPair(),
})

const createEmptyBodyVisual = (): CTHBodyVisual => ({
  slipUpsetArea: '',
  mpiExternal: '',
})

export const CTH_TEST_SITES = ['Mongstad', 'Ågotnes', 'Bergen'] as const
export type CTHTestSite = (typeof CTH_TEST_SITES)[number]
export const CTH_DEFAULT_TEST_SITE: CTHTestSite = 'Mongstad'

export const createDefaultCTHFormData = (): CTHFormData => ({
  testDate: new Date().toISOString().split('T')[0],
  testSite: CTH_DEFAULT_TEST_SITE,
  company: '',
  reportNumber: '',
  report_code: null,
  objectDescription: '',
  assemblyDescription: '',
  material: 'Carbon Steel',
  serialNo: '',
  partNo: '',
  comments: CTH_INSPECTION_COMMENTS,
  inspectionKind: 'CTH',
  inspectedSides: 'both',
  pin: createEmptyPinDimensional(),
  box: createEmptyBoxDimensional(),
  bodyToolLength: '',
  bodyOd: '',
  visualPin: createEmptyPinVisual(),
  visualBox: createEmptyBoxVisual(),
  visualBody: createEmptyBodyVisual(),
  pinRecommendation: '',
  boxRecommendation: '',
  issuedBy: '',
  verifiedBy: '',
  attachedImage: undefined,
})

function visualResults(pair: CTHVisualPair, requireNo2: boolean): CTHVisualResult[] {
  return requireNo2 ? [pair.no1, pair.no2] : [pair.no1]
}

function allExplicitOk(results: CTHVisualResult[]): boolean {
  return results.every((value) => value === 'OK')
}

function anyRejected(results: CTHVisualResult[]): boolean {
  return results.some((value) => value === 'REJ')
}

function allAnswered(results: CTHVisualResult[]): boolean {
  return results.every((value) => value === 'OK' || value === 'REJ')
}

export function pinVisualResults(visual: CTHPinVisual, kind: ThreadInspectionKind, requireNo2: boolean): CTHVisualResult[] {
  return visualRowsForKind(kind).flatMap((row) => visualResults(visual[row.pinKey], requireNo2))
}

export function boxVisualResults(visual: CTHBoxVisual, kind: ThreadInspectionKind, requireNo2: boolean): CTHVisualResult[] {
  return visualRowsForKind(kind).flatMap((row) => row.boxFixed === 'N/A' ? [] : visualResults(visual[row.boxKey], requireNo2))
}

export function bodyVisualResults(visual: CTHBodyVisual): CTHVisualResult[] {
  return [visual.slipUpsetArea, visual.mpiExternal]
}

export function isPinVisualAccepted(visual: CTHPinVisual, kind: ThreadInspectionKind, requireNo2 = false): boolean {
  return allExplicitOk(pinVisualResults(visual, kind, requireNo2))
}

export function isPinVisualRejected(visual: CTHPinVisual, kind: ThreadInspectionKind, requireNo2 = false): boolean {
  return anyRejected(pinVisualResults(visual, kind, requireNo2))
}

export function isBoxVisualAccepted(visual: CTHBoxVisual, kind: ThreadInspectionKind, requireNo2 = false): boolean {
  return allExplicitOk(boxVisualResults(visual, kind, requireNo2))
}

export function isBoxVisualRejected(visual: CTHBoxVisual, kind: ThreadInspectionKind, requireNo2 = false): boolean {
  return anyRejected(boxVisualResults(visual, kind, requireNo2))
}

export function isBodyVisualAccepted(visual: CTHBodyVisual): boolean {
  return allExplicitOk(bodyVisualResults(visual))
}

function sidesNote(parts: string[]): string {
  if (parts.length === 0) return ''
  return `(${parts.join(' + ')})`
}

export function computeCTHTestSummary(form: Pick<
  CTHFormData,
  'visualPin' | 'visualBox' | 'visualBody' | 'pinRecommendation' | 'boxRecommendation' | 'inspectedSides' | 'inspectionKind'
>, options?: { pinNo2?: boolean; boxNo2?: boolean }): {
  pinOk: boolean
  boxOk: boolean
  bodyOk: boolean
  pinRejected: boolean
  boxRejected: boolean
  bodyRejected: boolean
  pinComplete: boolean
  boxComplete: boolean
  bodyComplete: boolean
  pinInspected: boolean
  boxInspected: boolean
  bodyInspected: boolean
  total: number
  accepted: number
  rejected: number
  acceptedLabel: string
  rejectedLabel: string
  acceptedNote: string
  refaceNote: string
  recutNote: string
  scrapNote: string
  reface: number
  recut: number
  scrap: number
} {
  const kind = form.inspectionKind ?? 'CTH'
  const pinInspected = cthIncludesPin(form.inspectedSides ?? 'both')
  const boxInspected = cthIncludesBox(form.inspectedSides ?? 'both')
  const bodyInspected = kind === 'RSC'
  const pinNo2 = options?.pinNo2 ?? false
  const boxNo2 = options?.boxNo2 ?? false
  const pinResults = pinVisualResults(form.visualPin, kind, pinNo2)
  const boxResults = boxVisualResults(form.visualBox, kind, boxNo2)
  const bodyResults = bodyVisualResults(form.visualBody)
  const total = (pinInspected ? 1 : 0) + (boxInspected ? 1 : 0)
  const pinOk = !pinInspected || allExplicitOk(pinResults)
  const boxOk = !boxInspected || allExplicitOk(boxResults)
  const bodyOk = !bodyInspected || allExplicitOk(bodyResults)
  const pinRejected = pinInspected && anyRejected(pinResults)
  const boxRejected = boxInspected && anyRejected(boxResults)
  const bodyRejected = bodyInspected && anyRejected(bodyResults)
  const pinComplete = !pinInspected || allAnswered(pinResults)
  const boxComplete = !boxInspected || allAnswered(boxResults)
  const bodyComplete = true
  const pinRec = pinRejected ? form.pinRecommendation : ''
  const boxRec = boxRejected ? form.boxRecommendation : ''
  const accepted = (pinInspected && pinOk ? 1 : 0) + (boxInspected && boxOk ? 1 : 0)
  const rejected = (pinRejected ? 1 : 0) + (boxRejected ? 1 : 0)
  const formatCount = (n: number) => (n === total ? String(n) : `${n} of ${total}`)
  const recCount = (value: CTHSideRecommendation) =>
    (pinRec === value ? 1 : 0) + (boxRec === value ? 1 : 0)
  const note = (pin: boolean, body: boolean, box: boolean) => sidesNote([
    ...(pin ? ['PIN'] : []),
    ...(body ? ['BODY'] : []),
    ...(box ? ['BOX'] : []),
  ])

  return {
    pinOk,
    boxOk,
    bodyOk,
    pinRejected,
    boxRejected,
    bodyRejected,
    pinComplete,
    boxComplete,
    bodyComplete,
    pinInspected,
    boxInspected,
    bodyInspected,
    total,
    accepted,
    rejected,
    acceptedLabel: formatCount(accepted),
    rejectedLabel: formatCount(rejected),
    acceptedNote: note(pinInspected && pinOk, false, boxInspected && boxOk),
    refaceNote: note(pinRec === 'reface', false, boxRec === 'reface'),
    recutNote: note(pinRec === 'recut', false, boxRec === 'recut'),
    scrapNote: note(pinRec === 'decommission', false, boxRec === 'decommission'),
    reface: recCount('reface'),
    recut: recCount('recut'),
    scrap: recCount('decommission'),
  }
}

export function mapCTHFormToPDFData(
  form: CTHFormData,
  reportNo: string,
  companyName?: string,
  options?: { pinNo2?: boolean; boxNo2?: boolean; operatorCertNo?: string },
): CTHThreadInspectionData {
  const summary = computeCTHTestSummary(form, options)
  const mpiInspection = buildMpiInspectionTable(form, options)
  return {
    companyName: companyName || '',
    objectDescription: form.objectDescription,
    reportNo,
    assemblyDescription: form.assemblyDescription,
    testSite: form.testSite,
    material: form.material,
    testDate: form.testDate,
    serialNo: form.serialNo,
    operator: form.issuedBy,
    partNo: form.partNo,
    testProcedure: CTH_TEST_PROCEDURE_ACCEPTANCE,
    acceptance: CTH_TEST_PROCEDURE_ACCEPTANCE,
    comments: CTH_INSPECTION_COMMENTS,
    inspectionKind: form.inspectionKind,
    inspectedSides: form.inspectedSides,
    pin: form.pin,
    box: form.box,
    bodyToolLength: form.inspectionKind === 'RSC' ? form.bodyToolLength : '',
    bodyOd: form.inspectionKind === 'RSC' ? form.bodyOd : '',
    pinConnection: { ...form.pin.connection },
    pinOD: { ...form.pin.od },
    boxConnection: { ...form.box.connection },
    boxOD: { ...form.box.od },
    visualPin: form.visualPin,
    visualBox: form.visualBox,
    visualBody: form.inspectionKind === 'RSC' ? form.visualBody : undefined,
    mpiInspection: mpiInspection.visible ? mpiInspection.rows : undefined,
    testAccepted: summary.accepted,
    testRejected: summary.rejected,
    testRefaceRecommended: summary.reface,
    testRecutRecommended: summary.recut,
    testScrapRecommended: summary.scrap,
    testAcceptedLabel: summary.acceptedLabel,
    testRejectedLabel: summary.rejectedLabel,
    testAcceptedNote: summary.acceptedNote,
    testRefaceNote: summary.refaceNote,
    testRecutNote: summary.recutNote,
    testScrapNote: summary.scrapNote,
    inspectorName: form.issuedBy,
    issuedBy: form.issuedBy,
    verifiedBy: form.verifiedBy,
    attachedImage: form.attachedImage,
  }
}
